import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InputText } from '@/components/shared/InputText';

interface BankDetailsFieldsProps {
    accountHolderName: string;
    onAccountHolderNameChange: (value: string) => void;
    cvr: string;
    onCvrChange: (value: string) => void;
    iban: string;
    onIbanChange: (value: string) => void;
    bic: string;
    onBicChange: (value: string) => void;
    // Create requires every field; Edit's are all-or-nothing (only enforced
    // at submit time), so its inputs are never individually marked required.
    required?: boolean;
    // Edit disables the inputs while the current values are still loading, so
    // the operator can't type into a field that is about to be pre-filled.
    disabled?: boolean;
    holderNameError?: boolean;
    cvrError?: boolean;
    ibanError?: boolean;
    bicError?: boolean;
}

// The four payout-sender bank-material inputs shared by CreateHouseAccountDialog
// and EditHouseAccountDialog — kept in one place so the fields, labels, and
// error wiring can't drift between the two forms.
export const BankDetailsFields = ({
    accountHolderName,
    onAccountHolderNameChange,
    cvr,
    onCvrChange,
    iban,
    onIbanChange,
    bic,
    onBicChange,
    required = false,
    disabled = false,
    holderNameError = false,
    cvrError = false,
    ibanError = false,
    bicError = false
}: BankDetailsFieldsProps) => {
    const { t } = useTranslation();

    return (
        <>
            <InputText
                testid="house-account-holder-name"
                label={t('house_accounts.account_holder_name')}
                required={required}
                disabled={disabled}
                value={accountHolderName}
                onChange={(event) =>
                    onAccountHolderNameChange(event.target.value)
                }
                error={holderNameError}
                helperText={
                    holderNameError
                        ? t('house_accounts.account_holder_name_required')
                        : undefined
                }
            />

            <Box mt={2}>
                <InputText
                    testid="house-account-cvr"
                    label={t('house_accounts.cvr')}
                    required={required}
                    disabled={disabled}
                    value={cvr}
                    onChange={(event) => onCvrChange(event.target.value)}
                    error={cvrError}
                    helperText={
                        cvrError ? t('house_accounts.cvr_invalid') : undefined
                    }
                />
            </Box>

            <Box mt={2}>
                <InputText
                    testid="house-account-iban"
                    label={t('house_accounts.iban')}
                    required={required}
                    disabled={disabled}
                    value={iban}
                    onChange={(event) => onIbanChange(event.target.value)}
                    error={ibanError}
                    helperText={
                        ibanError ? t('house_accounts.iban_invalid') : undefined
                    }
                />
            </Box>

            <Box mt={2}>
                <InputText
                    testid="house-account-bic"
                    label={t('house_accounts.bic')}
                    required={required}
                    disabled={disabled}
                    value={bic}
                    onChange={(event) => onBicChange(event.target.value)}
                    error={bicError}
                    helperText={
                        bicError ? t('house_accounts.bic_invalid') : undefined
                    }
                />
            </Box>
        </>
    );
};

export default BankDetailsFields;
